import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { SortableHeader, PaginationBar } from "../../components/DataTable";
import AdminSelect from "../../components/AdminSelect";
import MultiSelect from "../../components/MultiSelect";
import { clientApi } from "../../api";
import { formatDateAU } from "../../utils/formatDate";

/**
 * Client-portal members landing. Sources from the venue-wide S7000
 * cache (with local portal fields overlaid) and supports server-side
 * search, filter, sort, and pagination. Free-text search against
 * S7000 for card-number lookups still routes through /members/search.
 */
export default function MembersList() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [providerType, setProviderType] = useState("s7000");
  const providerLabel = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan" }[providerType] || "S7000";
  // Only S7000/IGT expose a live card-number lookup; for cache-only providers
  // (CircleScan/SwiftPOS) hide the "(live)" card field and the suspension
  // filter, which key on concepts those providers don't have.
  const supportsLiveCard = providerType === "igt" || providerType === "s7000";
  const supportsSuspension = providerType === "igt" || providerType === "s7000";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Per-field searches — one box per column (replaces the old single combined
  // box that confusingly searched name/email/member-# all at once).
  const [name, setName] = useState("");
  const [memberNo, setMemberNo] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [card, setCard] = useState("");
  const [scope, setScope] = useState("all");         // "all" | "portal"
  const [status, setStatus] = useState("all");       // "all" | "active" | "suspended"
  // Column filters — AND-combined with search + scope.
  const [products, setProducts] = useState([]);      // venue product catalogue
  const [productIds, setProductIds] = useState([]);  // selected product ids (strings)
  const [expiry, setExpiry] = useState("any");       // any|expired|soon30|soon90|none
  const [contact, setContact] = useState([]);        // subset of ["email","mobile"]
  // Newest signups first by default — sort on the portal registration instant
  // (date + time) so the most recent joins are at the top.
  const [sort, setSort] = useState({ by: "joined", dir: "desc" });
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Card # is a live provider lookup (7+ digits) that swaps the grid for the
  // single-card result. The other fields filter the cached list.
  const isCardNumber = /^\d{7,}$/.test(card.trim());
  const [cardResult, setCardResult] = useState(null);
  const [cardErr, setCardErr] = useState("");

  useEffect(() => { setOffset(0); }, [name, memberNo, email, mobile, scope, status, productIds, expiry, contact, sort.by, sort.dir, limit]);

  useEffect(() => {
    if (isCardNumber) return; // handled by a separate effect below
    let alive = true;
    const handle = setTimeout(async () => {
      setLoading(true); setError("");
      try {
        const params = {
          scope, status,
          sort_by: sort.by, sort_dir: sort.dir,
          limit, offset,
        };
        if (name.trim())     params.name = name.trim();
        if (memberNo.trim()) params.member_number = memberNo.trim();
        if (email.trim())    params.email = email.trim();
        if (mobile.trim())   params.mobile = mobile.trim();
        if (productIds.length) params.products = productIds.join(",");
        if (expiry !== "any") params.expiry = expiry;
        if (contact.includes("email")) params.has_email = "1";
        if (contact.includes("mobile")) params.has_mobile = "1";
        const d = await clientApi.getMembers(params);
        if (!alive) return;
        setRows(d.members || []); setTotal(d.total || 0);
        if (d.provider_type) setProviderType(d.provider_type);
      } catch (err) { if (alive) setError(err.message); }
      if (alive) setLoading(false);
    }, 250);
    return () => { alive = false; clearTimeout(handle); };
  }, [name, memberNo, email, mobile, scope, status, productIds, expiry, contact, sort.by, sort.dir, limit, offset, isCardNumber]);

  // Card-number live lookup (7+ digits) — bypasses the cache.
  useEffect(() => {
    if (!isCardNumber) { setCardResult(null); setCardErr(""); return; }
    let alive = true;
    const handle = setTimeout(async () => {
      try {
        const r = await clientApi.searchMembers(card);
        if (alive) setCardResult(r);
      } catch (err) { if (alive) { setCardErr(err.message); setCardResult(null); } }
    }, 350);
    return () => { alive = false; clearTimeout(handle); };
  }, [card, isCardNumber]);

  // Venue product catalogue — populates the membership-type filter. Loaded
  // once; harmless to fetch even for venues with no products configured.
  useEffect(() => {
    let alive = true;
    clientApi.getProducts()
      .then((p) => { if (alive) setProducts(Array.isArray(p) ? p : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Campaign/UTM attribution now lives on the dedicated Acquisition report
  // (client portal → Reports → Acquisition), which owns the filtered CSV export.

  // Active-filter chips for the summary row (scope + free-text search are
  // surfaced by their own controls, so they're intentionally excluded here).
  const productById = Object.fromEntries(products.map((p) => [String(p.id), p.name]));
  const EXPIRY_LABELS = {
    expired: "Expired",
    soon30: "Expiring ≤ 30 days",
    soon90: "Expiring ≤ 90 days",
    none: "No expiry set",
  };
  const activeChips = [];
  if (status !== "all")
    activeChips.push({ key: "status", label: `Status: ${status === "active" ? "Active" : "Suspended"}`, onRemove: () => setStatus("all") });
  productIds.forEach((id) =>
    activeChips.push({ key: `prod-${id}`, label: productById[String(id)] || `Product ${id}`, onRemove: () => setProductIds((cur) => cur.filter((x) => x !== id)) }));
  if (expiry !== "any")
    activeChips.push({ key: "expiry", label: EXPIRY_LABELS[expiry] || expiry, onRemove: () => setExpiry("any") });
  if (contact.includes("email"))
    activeChips.push({ key: "c-email", label: "Has email", onRemove: () => setContact((cur) => cur.filter((x) => x !== "email")) });
  if (contact.includes("mobile"))
    activeChips.push({ key: "c-mobile", label: "Has mobile", onRemove: () => setContact((cur) => cur.filter((x) => x !== "mobile")) });
  // Active per-field search chips, shown alongside the filter chips.
  [
    { v: name,     set: setName,     label: "Name",     key: "s-name" },
    { v: memberNo, set: setMemberNo, label: "Member #", key: "s-mno" },
    { v: email,    set: setEmail,    label: "Email",    key: "s-email" },
    { v: mobile,   set: setMobile,   label: "Mobile",   key: "s-mobile" },
  ].forEach((c) => { if (c.v.trim()) activeChips.push({ key: c.key, label: `${c.label}: ${c.v.trim()}`, onRemove: () => c.set("") }); });
  const clearAllFilters = () => { setName(""); setMemberNo(""); setEmail(""); setMobile(""); setStatus("all"); setProductIds([]); setExpiry("any"); setContact([]); };

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Members</h1>
          <p className="page-subtitle">
            Full venue member list from {providerLabel}.
            {" "}
            {providerType === "igt" || providerType === "s7000"
              ? `Card-number search hits ${providerLabel} live.`
              : "Search runs against the cached member list."}
          </p>
        </div>
      </div>

      <div className="venues-summary" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="venues-summary-counts" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span><strong>{total.toLocaleString()}</strong> {scope === "portal" ? "portal-managed" : "total"} members</span>
          <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
            <button className={`btn btn-sm ${scope === "all" ? "btn-primary" : "btn-outline"}`} style={{ borderRadius: 0, border: "none" }} aria-pressed={scope === "all"} onClick={() => setScope("all")}>All {providerLabel}</button>
            <button className={`btn btn-sm ${scope === "portal" ? "btn-primary" : "btn-outline"}`} style={{ borderRadius: 0, border: "none" }} aria-pressed={scope === "portal"} onClick={() => setScope("portal")}>Portal</button>
          </div>
        </div>
      </div>

      <div className="filter-bar">
            <span className="filter-bar-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
              Search &amp; filter
            </span>

            <div className="filter-field">
              <span className="filter-field-label">Name</span>
              <input className="filter-search-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Search name…" autoFocus />
            </div>
            <div className="filter-field">
              <span className="filter-field-label">Member #</span>
              <input className="filter-search-input" value={memberNo} onChange={(e) => setMemberNo(e.target.value)} placeholder="e.g. 6119" inputMode="numeric" />
            </div>
            <div className="filter-field">
              <span className="filter-field-label">Email</span>
              <input className="filter-search-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Search email…" />
            </div>
            <div className="filter-field">
              <span className="filter-field-label">Mobile</span>
              <input className="filter-search-input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Search mobile…" inputMode="tel" />
            </div>
            {supportsLiveCard && (
              <div className="filter-field">
                <span className="filter-field-label">Card # (live)</span>
                <input className="filter-search-input" value={card} onChange={(e) => setCard(e.target.value)} placeholder="7+ digits…" inputMode="numeric" title="Card-number lookup hits the provider live" />
              </div>
            )}

            <div className="filter-field">
              <span className="filter-field-label">Status</span>
              <div className="filter-select-compact">
                <AdminSelect
                  value={status}
                  onChange={setStatus}
                  options={[
                    { value: "all", label: "All statuses" },
                    { value: "active", label: "Active only" },
                    ...(supportsSuspension ? [{ value: "suspended", label: "Suspended only" }] : []),
                  ]}
                />
              </div>
            </div>

            {products.length > 0 && (
              <div className="filter-field">
                <span className="filter-field-label">Membership</span>
                <div className="filter-select-compact">
                  <MultiSelect
                    value={productIds}
                    onChange={setProductIds}
                    placeholder="All types"
                    options={products.map((p) => ({ value: String(p.id), label: p.name }))}
                  />
                </div>
              </div>
            )}

            <div className="filter-field">
              <span className="filter-field-label">Expiry</span>
              <div className="filter-select-compact">
                <AdminSelect
                  value={expiry}
                  onChange={setExpiry}
                  options={[
                    { value: "any", label: "Any expiry" },
                    { value: "expired", label: "Expired" },
                    { value: "soon30", label: "Expiring ≤ 30 days" },
                    { value: "soon90", label: "Expiring ≤ 90 days" },
                    { value: "none", label: "No expiry set" },
                  ]}
                />
              </div>
            </div>

            <div className="filter-field">
              <span className="filter-field-label">Contact</span>
              <div className="filter-select-compact">
                <MultiSelect
                  value={contact}
                  onChange={setContact}
                  placeholder="Any"
                  options={[
                    { value: "email", label: "Has email" },
                    { value: "mobile", label: "Has mobile" },
                  ]}
                />
              </div>
            </div>
      </div>

      {activeChips.length > 0 && (
        <div className="filter-chips">
          {activeChips.map((c) => (
            <span key={c.key} className="filter-chip">
              {c.label}
              <button type="button" aria-label={`Remove ${c.label}`} onClick={c.onRemove}>×</button>
            </span>
          ))}
          <button type="button" className="filter-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {isCardNumber ? (
        <CardLookupResult q={card} result={cardResult} error={cardErr} providerType={providerType} />
      ) : error ? (
        <div className="toast toast-error" style={{ margin: "1rem 0" }}>{error}</div>
      ) : (
        <>
          <MembersTable rows={rows} loading={loading} sort={sort} setSort={setSort} providerType={providerType} />
          <PaginationBar
            total={total}
            limit={limit}
            offset={offset}
            onLimit={setLimit}
            onOffset={setOffset}
          />
        </>
      )}
    </ClientLayout>
  );
}

function MembersTable({ rows, loading, sort, setSort, providerType = "s7000" }) {
  const providerLabel = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan" }[providerType] || "S7000";
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <SortableHeader columnKey="surname"       sort={sort} onSort={setSort}>Member</SortableHeader>
            <SortableHeader columnKey="membernumber"  sort={sort} onSort={setSort}>Member #</SortableHeader>
            <SortableHeader columnKey="email"         sort={sort} onSort={setSort}>Email</SortableHeader>
            <th>Mobile</th>
            <th>Status</th>
            <th>Expiry</th>
            <SortableHeader columnKey="joined" sort={sort} onSort={setSort}>Registered</SortableHeader>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="8" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>Loading…</td></tr>
          ) : !rows.length ? (
            <tr><td colSpan="8" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>No members found. Try clearing filters, or ask an admin to run a sync.</td></tr>
          ) : rows.map((m) => {
            // Subtitle under the member's name — show the membership type
            // the user actually selected and paid for (from `venue_products`
            // via members.product_id). Falls through to a dash when the
            // member isn't portal-managed, i.e. no product on file.
            const subtitle = m.product_name
              ? m.product_name
              : (m.portal_managed ? "No product recorded" : "Not portal-managed");
            return (
              <tr key={m.id}>
                <td>
                  <Link to={`/client/members/${m.membership_id}`} style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "inherit", textDecoration: "none" }}>
                    <div className="ct-avatar">{(m.first_name?.[0] || "").toUpperCase()}{(m.last_name?.[0] || "").toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{m.first_name || ""} {m.last_name || ""}</div>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{subtitle}</div>
                    </div>
                  </Link>
                </td>
                <td><code style={{ fontSize: "0.8rem", background: "var(--bg-elevated)", padding: "0.2rem 0.5rem", borderRadius: 4 }}>#{m.membership_id}</code></td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{m.email || "—"}</td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{m.mobile || "—"}</td>
                <td><MemberStatusBadge m={m} /></td>
                <td>{m.date_expiry ? <span className={`badge ${new Date(m.date_expiry) < new Date() ? "inactive" : "active"}`}>{formatDate(m.date_expiry)}</span> : <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{m.portal_registered_at ? formatDate(m.portal_registered_at) : "—"}</td>
                <td>
                  {m.portal_managed
                    ? <span className="badge active" style={{ fontSize: "0.7rem" }}>Portal</span>
                    : <span className="badge" style={{ fontSize: "0.7rem", background: "var(--bg-elevated)", color: "var(--text-muted)" }}>{providerLabel}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CardLookupResult({ q, result, error, providerType = "s7000" }) {
  const label = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan" }[providerType] || "S7000";
  if (error) return <div className="toast toast-error" style={{ margin: "1rem 0" }}>{error}</div>;
  if (!result) return <div style={{ color: "var(--text-muted)", marginTop: "1rem" }}>Looking up card "{q}" in {label}…</div>;
  const rows = result.results || [];
  if (!rows.length) return (
    <div className="empty-state" style={{ marginTop: "1rem" }}>
      <h3>No matches</h3>
      <p>Searched {label} by card number for "{q}". Check the digits and try again.</p>
    </div>
  );
  return (
    <div className="table-wrap" style={{ marginTop: "1rem" }}>
      <table className="data-table">
        <thead><tr><th>Member #</th><th>Name</th><th>Email</th><th>Mobile</th></tr></thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={m.membershipId || i}>
              <td><Link to={`/client/members/${m.membershipId}`}><code>#{m.membershipId}</code></Link></td>
              <td>{m.firstName} {m.lastName}</td>
              <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{m.email || "—"}</td>
              <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{m.mobile || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Provider-neutral status badge. S7000/IGT carry a boolean suspension flag;
// CircleScan/SwiftPOS derive standing from membership expiry, so fall back to
// an Expired state rather than labelling every member "Active".
function MemberStatusBadge({ m }) {
  if (m.suspensionflag === "1") return <span className="badge inactive">Suspended</span>;
  const exp = m.date_expiry ? new Date(m.date_expiry) : null;
  if (exp && !isNaN(exp) && exp < new Date()) return <span className="badge pending">Expired</span>;
  return <span className="badge active">Active</span>;
}

function formatDate(v) {
  return formatDateAU(v);
}
